import { useEffect, useRef, useState } from "react"
import {
  Box,
  Button,
  Dialog,
  IconButton,
  Stack,
  Typography,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import { useNavigate } from "react-router-dom"
import { getUserInfo } from "../../utils/authDecode"

export default function SessionExpiredModal() {
  const navigate = useNavigate()
  const [isExpiredOpen, setIsExpiredOpen] = useState(false)
  const [isWarningOpen, setIsWarningOpen] = useState(false)
  const [expiredRoles, setExpiredRoles] = useState([])
  const warningShownForExpRef = useRef(null)

  useEffect(() => {
    const warningWindowMs = 2 * 60 * 1000

    const evaluateSession = () => {
      const token = sessionStorage.getItem("token")
      if (!token) {
        setIsWarningOpen(false)
        warningShownForExpRef.current = null
        return
      }

      const userInfo = getUserInfo(token)
      const exp = Number(userInfo?.exp)
      const roles = Array.isArray(userInfo?.role) ? userInfo.role : []

      if (!Number.isFinite(exp)) {
        setIsWarningOpen(false)
        return
      }

      const expiresAtMs = exp * 1000
      const timeLeftMs = expiresAtMs - Date.now()

      if (timeLeftMs > warningWindowMs) {
        setIsWarningOpen(false)
        warningShownForExpRef.current = null
        return
      }

      if (timeLeftMs <= 0) {
        setExpiredRoles(roles)
        setIsWarningOpen(false)
        sessionStorage.removeItem("token")
        setIsExpiredOpen(true)
        return
      }

      if (warningShownForExpRef.current !== exp) {
        setIsWarningOpen(true)
        warningShownForExpRef.current = exp
      }
    }

    evaluateSession()
    const timer = window.setInterval(evaluateSession, 1000)

    return () => window.clearInterval(timer)
  }, [])

  const handleExpiredClose = () => {
    setIsExpiredOpen(false)
  }

  const handleWarningClose = () => {
    setIsWarningOpen(false)
  }

  const handleBackHome = () => {
    setIsExpiredOpen(false)
    navigate("/home")
  }

  const handleLogin = () => {
    setIsExpiredOpen(false)

    if (expiredRoles.includes("superadmin")) {
      navigate("/system-admin-login")
      return
    }

    if (expiredRoles.includes("tenant_admin")) {
      navigate("/login?role=partner")
      return
    }

    if (expiredRoles.includes("customer")) {
      navigate("/login?role=customer")
      return
    }

    navigate("/login?role=partner")
  }

  const handleLogOff = () => {
    sessionStorage.removeItem("token")
    setIsWarningOpen(false)
    warningShownForExpRef.current = null
    navigate("/home")
  }

  const handleContinue = () => {
    setIsWarningOpen(false)
  }

  return (
    <>
      <Dialog
        open={isWarningOpen}
        onClose={handleWarningClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "8px",
            p: 0.5,
          },
        }}
      >
        <Box sx={{ p: 2.5, pb: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              mb: 1.25,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, pr: 1 }}>
              Your session is about to expire
            </Typography>
            <IconButton
              onClick={handleWarningClose}
              size="small"
              sx={{ mt: -0.5 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Typography variant="body2" sx={{ color: "text.subcaption", mb: 2.5 }}>
            Your session will expire soon. Do you wish to extend?
          </Typography>

          <Stack direction="row" spacing={1.5}>
            <Button
              variant="outlined"
              fullWidth
              onClick={handleLogOff}
              sx={{
                borderColor: "text.inverse",
                color: "text.heading",
                "&:hover": { backgroundColor: "#f0f0f0" },
                borderRadius: "8px",
              }}
            >
              Log off
            </Button>
            <Button
              variant="contained"
              fullWidth
              onClick={handleContinue}
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                bgcolor: "accent.main",
              }}
            >
              Continue
            </Button>
          </Stack>
        </Box>
      </Dialog>

      <Dialog
        open={isExpiredOpen}
        onClose={handleExpiredClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "8px",
            p: 0.5,
          },
        }}
      >
        <Box sx={{ p: 2.5, pb: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              mb: 1.25,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, pr: 1 }}>
              Your Session has Expired
            </Typography>
            <IconButton onClick={handleExpiredClose} size="small" sx={{ mt: -0.5 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Typography variant="body2" sx={{ color: "text.subcaption", mb: 2.5 }}>
            Please log in again to continue using PrymeBooking.
          </Typography>

          <Stack direction="row" spacing={1.5}>
            <Button
              variant="outlined"
              fullWidth
              onClick={handleBackHome}
              sx={{
                borderColor: "text.inverse",
                color: "text.heading",
                "&:hover": { backgroundColor: "#f0f0f0" },
                borderRadius: "8px",
              }}
            >
              Back to Home
            </Button>
            <Button
              variant="contained"
              fullWidth
              onClick={handleLogin}
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                bgcolor: "accent.main",
              }}
            >
              Log in
            </Button>
          </Stack>
        </Box>
      </Dialog>
    </>
  )
}
